import { Handle, Position, type Node as FlowNode, type NodeProps, useReactFlow } from '@xyflow/react';
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState, useEffect, useRef, useCallback, useMemo, type CSSProperties } from 'react';
import { AutomationStepIcon } from '../../../components/svgs/icons/AutomationStepIcon';
import { AutomationTemplateStepIndicatorIcon } from '../../../components/svgs/icons/AutomationTemplateStepIndicator';
import type { BaseNodeData } from '../types';
import type { AutomationStepNodeData } from '../types/stepNodes';
import { STEP_NODE_COMPACT_HEIGHT } from '../utils/layoutConstants';
import { isAutomationStepNodeData } from '../types/typeGuards';
import { AutomationStepCardAdapter } from './AutomationStepCardAdapter';
import { stepNodeStyles } from './stepNodeStyles';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';
import { BRANCH_COLORS } from '../edges/branchEdgeStyles';
import { getBehaviorScheduleAnchorDescription } from '../utils/behaviorSchedule';
import { getStepTypeBorderColor } from '../utils/stepTypeColors';
import { getTriggerDescription } from '../../../components/automation/AutomationStepCard/presentation';
import { shouldUseAnchoredSchedule } from '../utils/triggerAutomationStepRules';
import { getDefaultTimeStringValue } from '../../../../models/utils/dateUtils';
import { useNodeHeightMeasurement } from '../hooks/useNodeHeightMeasurement';

function getStepTypeModelName(stepType: Api.AutomationStepType | string): Api.AutomationStepModelType {
	const result = (() => {
		switch (stepType) {
			case Api.AutomationStepType.Email:
				return 'EmailAutomationStep';
			case Api.AutomationStepType.Texting:
				return 'TextingAutomationStep';
			case Api.AutomationStepType.ActionItem:
				return 'ActionItemAutomationStep';
			case Api.AutomationStepType.AddTag:
				return 'AddTagAutomationStep';
			case Api.AutomationStepType.RemoveTag:
				return 'RemoveTagAutomationStep';
			case Api.AutomationStepType.HandwrittenCard:
				return 'HandwrittenCardAutomationStep';
			case Api.AutomationStepType.NoAction:
				return 'NoActionAutomationStep';
			case Api.AutomationStepType.Opportunity:
				return 'OpportunityAutomationStep';
			case Api.AutomationStepType.HtmlNewsletter:
				return 'HtmlNewsletterAutomationStep';
			case Api.AutomationStepType.Switch:
				return 'SwitchAutomationStep';
			case 'TriggerAutomation':
				return 'TriggerAutomationStep' as Api.AutomationStepModelType;
			default:
				return 'EmailAutomationStep';
		}
	})();

	return result;
}

function getDefaultStepName(stepType: Api.AutomationStepType | string): string {
	switch (stepType) {
		case Api.AutomationStepType.Email:
			return 'Send an email';
		case Api.AutomationStepType.Texting:
			return 'Send a text';
		case Api.AutomationStepType.ActionItem:
			return 'Create an action item';
		case Api.AutomationStepType.AddTag:
			return 'Add a tag';
		case Api.AutomationStepType.RemoveTag:
			return 'Remove a tag';
		case Api.AutomationStepType.HandwrittenCard:
			return 'Send a card';
		case Api.AutomationStepType.NoAction:
			return 'Do nothing';
		case Api.AutomationStepType.Opportunity:
			return 'Create or update opportunity';
		case Api.AutomationStepType.HtmlNewsletter:
			return 'Send HTML newsletter';
		case Api.AutomationStepType.Switch:
			return 'Conditional branch';
		case 'TriggerAutomation':
			return 'Start automation';
		default:
			return 'Automation step';
	}
}

/** Derives the display name for a step node from its pre-computed stepData and type. */
function getStepDisplayName(stepNodeData: AutomationStepNodeData): string {
	if (stepNodeData.stepType === Api.AutomationStepType.NoAction) {
		return 'Do nothing';
	}

	const rawStepName = (stepNodeData.stepData?.name || stepNodeData.automationStep?.name || '').trim();

	if (
		stepNodeData.stepType === Api.AutomationStepType.AddTag ||
		stepNodeData.stepType === Api.AutomationStepType.RemoveTag
	) {
		const tagStep = stepNodeData.automationStep as
			| (Api.IAddTagAutomationStep & { _type: 'AddTagAutomationStep' })
			| (Api.IRemoveTagAutomationStep & { _type: 'RemoveTagAutomationStep' })
			| undefined;
		const tagName = tagStep?.tagName?.trim();
		const prefix = stepNodeData.stepType === Api.AutomationStepType.AddTag ? 'Add Tag' : 'Remove Tag';
		if (tagName) {
			return `${prefix}: ${tagName}`;
		}
		return rawStepName || prefix;
	}

	if (stepNodeData.stepType === Api.AutomationStepType.ActionItem) {
		if (!rawStepName) {
			return getDefaultStepName(stepNodeData.stepType);
		}
		if (rawStepName.toLowerCase().startsWith('action item:')) {
			return rawStepName;
		}
		return `Action Item: ${rawStepName}`;
	}

	return rawStepName || getDefaultStepName(stepNodeData.stepType);
}

function formatScheduleSummary(
	schedule?: Api.IAutomationStepSchedule,
	anchorDescription = 'trigger',
	usesRelativeTimeOffsets = false
): string {
	if (!schedule) {
		return 'No schedule set';
	}

	if (schedule.criteria === Api.AutomationStepScheduleCriteria.Immediately) {
		return 'Immediately';
	}

	const days = schedule.numberOfDays || 0;
	const hours = schedule.numberOfHours || 0;
	const minutes = schedule.numberOfMinutes || 0;

	const formatUnit = (value: number, unit: string): string => {
		const magnitude = Math.abs(value);
		const plural = magnitude !== 1 ? 's' : '';
		const direction = value < 0 ? 'before' : 'after';
		return `${magnitude} ${unit}${plural} ${direction} the ${anchorDescription}`;
	};

	if (days !== 0) {
		return formatUnit(days, 'day');
	}

	if (usesRelativeTimeOffsets || hours < 0 || minutes < 0) {
		if (hours !== 0) {
			return formatUnit(hours, 'hour');
		}
		if (minutes !== 0) {
			return formatUnit(minutes, 'minute');
		}
		return 'Immediately';
	}

	const hasExplicitTime = typeof schedule.numberOfHours === 'number' || typeof schedule.numberOfMinutes === 'number';
	if (!hasExplicitTime || (hours === 0 && minutes === 0)) {
		return `same day as the ${anchorDescription}`;
	}

	const timeOfDay = new Date();
	timeOfDay.setHours(hours, minutes, 0, 0);
	return `same day as the ${anchorDescription} at ${getDefaultTimeStringValue(timeOfDay)}`;
}

function CollapseIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M12 10L8 6L4 10' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

function ExpandIcon() {
	return (
		<svg width='16' height='16' viewBox='0 0 16 16' fill='none'>
			<path d='M4 6L8 10L12 6' stroke='currentColor' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
		</svg>
	);
}

export function AutomationStepNodeWrapper({ data, id, selected }: NodeProps) {
	// Runtime assertion to ensure data structure is valid
	if (!isAutomationStepNodeData(data)) {
		const debugSnapshot = {
			nodeId: id,
			typeofData: typeof data,
			keys: data && typeof data === 'object' ? Object.keys(data as Record<string, unknown>) : null,
			data,
			stack: new Error('AutomationStepNodeWrapper debug stack').stack,
		};
		console.error('[AutomationStepNodeWrapper] Invalid node data payload', debugSnapshot);
		throw new Error(`Invalid AutomationStepNodeData structure for node ${id}`);
	}

	const stepNodeData = data;
	const [isExpanded, setIsExpanded] = useState(stepNodeData.isExpanded);

	// Sync local state when parent updates data.isExpanded (e.g., Ctrl+E bulk toggle).
	useEffect(() => {
		setIsExpanded(stepNodeData.isExpanded);
	}, [stepNodeData.isExpanded]);

	const {
		isReadOnly: contextIsReadOnly,
		updateNodeData,
		automationTemplate: rootAutomationTemplate,
	} = useAutomationFlowContext();
	const isReadOnly = contextIsReadOnly || stepNodeData.isReadOnly === true;
	const { setNodes } = useReactFlow();

	// Helper to update both local state and persisted node data.
	// When collapsing, clear estimatedHeight so the layout engine does not use a
	// stale expanded height for positioning downstream nodes.
	const setExpanded = useCallback(
		(expanded: boolean) => {
			setIsExpanded(expanded);
			if (expanded) {
				updateNodeData?.(id, { isExpanded: true, isCompact: false });
			} else {
				// Set estimatedHeight to compact baseline so the layout engine uses the
				// correct height immediately, rather than waiting for ResizeObserver to
				// report the collapsed dimension (which can lag by 1-2 frames and cause
				// a layout "blip" where downstream nodes briefly use the old expanded height).
				updateNodeData?.(id, { isExpanded: false, isCompact: true, estimatedHeight: STEP_NODE_COMPACT_HEIGHT });
			}
		},
		[id, updateNodeData]
	);

	// Ref for measuring content height
	const contentRef = useRef<HTMLDivElement>(null);

	// Prefer the canvas-level updateNodeData callback; fall back to direct setNodes
	// for legacy contexts that don't provide it.
	const heightUpdateCallback = useMemo(
		() =>
			updateNodeData ??
			((nodeId: string, nextData: Record<string, unknown>) => {
				setNodes(
					currentNodes =>
						currentNodes.map(node =>
							node.id === nodeId ? { ...node, data: { ...node.data, ...nextData } } : node
						) as FlowNode<BaseNodeData>[]
				);
			}),
		[updateNodeData, setNodes]
	);

	useNodeHeightMeasurement(contentRef, id, isExpanded, stepNodeData.estimatedHeight || null, heightUpdateCallback);

	const stepTypeName =
		(stepNodeData.automationStep?._type as string | undefined) || getStepTypeModelName(stepNodeData.stepType);
	const stepName = getStepDisplayName(stepNodeData);

	const contactColors = stepNodeData.contactColors;
	const contactCount = stepNodeData.contactCount;

	// Support multiple output handles for switch nodes
	const outputHandles = stepNodeData.outputHandles || ['default'];
	const hasMultipleOutputs = outputHandles.length > 1;
	const sourcePosition = stepNodeData.sourcePosition || Position.Bottom;
	const targetPosition = stepNodeData.targetPosition || Position.Top;
	const isHorizontal = sourcePosition === Position.Left || sourcePosition === Position.Right;

	// Calculate handle positions for multiple outputs
	const calculateHandleStyle = (index: number, total: number) => {
		if (total === 1) {
			return isHorizontal ? { top: '50%' } : { left: '50%' };
		}
		const percentage = (100 / (total + 1)) * (index + 1);
		return isHorizontal ? { top: `${percentage}%` } : { left: `${percentage}%` };
	};

	// Get parent case info for visual indicator
	const parentCaseInfo = stepNodeData.parentCaseInfo;
	const isInvalid = !stepNodeData.isValid;
	const validationPathState = stepNodeData.validationPathState;

	// Border color priority: invalid state (red from CSS class) > case color > step type color
	// Only apply inline border color when not invalid, so the CSS class can set red border
	const borderColor = parentCaseInfo?.caseColor || getStepTypeBorderColor(stepNodeData.stepType);
	const cardBorderStyle: CSSProperties | undefined = isInvalid ? undefined : { borderColor };

	const rootTrigger =
		rootAutomationTemplate?.draft?.trigger ||
		rootAutomationTemplate?.published?.trigger ||
		stepNodeData.automationTemplate?.draft?.trigger ||
		stepNodeData.automationTemplate?.published?.trigger;
	const behaviorScheduleAnchorDescription = getBehaviorScheduleAnchorDescription(stepNodeData.parentCaseInfo);
	const shouldUseBehaviorScheduleAnchor =
		stepNodeData.parentCaseInfo?.isBehaviorCase === true && !shouldUseAnchoredSchedule(rootTrigger);
	const scheduleAnchorDescription =
		(shouldUseBehaviorScheduleAnchor ? behaviorScheduleAnchorDescription : undefined) ||
		getTriggerDescription(rootTrigger) ||
		'trigger';
	const behaviorCaseDescription = behaviorScheduleAnchorDescription || scheduleAnchorDescription;
	const usesRelativeTimeOffsets =
		rootTrigger?._type === Api.AutomationTriggerType.Meeting ||
		rootTrigger?._type === Api.AutomationTriggerType.EventRegistration;
	const scheduleText = stepNodeData.stepData?.schedule
		? formatScheduleSummary(stepNodeData.stepData.schedule, scheduleAnchorDescription, usesRelativeTimeOffsets)
		: '';
	const ariaLabel = `${stepName} automation step${scheduleText ? `, scheduled ${scheduleText}` : ''}${isInvalid ? ', configuration incomplete' : ''}`;
	const parentCaseOrdinal = parentCaseInfo?.globalSegmentOrdinal || (parentCaseInfo?.caseIndex || 0) + 1;
	const behaviorBadgeLabel = (() => {
		const caseName = parentCaseInfo?.caseName || '';
		if (/timeout/i.test(caseName) || /^if\s+no\s+engagement/i.test(caseName)) {
			return 'TIMEOUT';
		}
		if (/repl(y|ies)/i.test(caseName)) {
			return 'REPLY';
		}
		if (/click/i.test(caseName)) {
			return 'CLICK';
		}
		if (/open/i.test(caseName)) {
			return 'OPEN';
		}
		return 'BEHAVIOR';
	})();
	const parentCaseBadgeLabel = parentCaseInfo?.isDefault
		? 'DEFAULT'
		: parentCaseInfo?.isBehaviorCase
			? behaviorBadgeLabel
			: `Segment #${parentCaseOrdinal}`;
	const parentCaseAriaLabel = parentCaseInfo?.isDefault
		? 'Part of default case'
		: parentCaseInfo?.isBehaviorCase
			? `Part of ${behaviorCaseDescription} case`
			: `Part of Segment #${parentCaseOrdinal}`;
	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			setExpanded(!isExpanded);
		}
	};

	// Helper function to get case color
	const getCaseColor = (caseIndex: number): string => {
		const switchStep = stepNodeData.automationStep as Api.ISwitchAutomationStep | undefined;
		const cases = switchStep?.cases || [];
		const caseStatement = cases[caseIndex];

		// Check if this is the default case
		if (caseStatement?.isDefault) {
			return '#6B7280'; // Gray for default
		}

		return BRANCH_COLORS[caseIndex % BRANCH_COLORS.length];
	};

	// Helper function to get case name
	const getCaseName = (caseIndex: number): string => {
		const switchStep = stepNodeData.automationStep as Api.ISwitchAutomationStep | undefined;
		const cases = switchStep?.cases || [];
		const caseStatement = cases[caseIndex];

		if (caseStatement?.isDefault) {
			return 'Default case';
		}

		return caseStatement?.name || `Case ${caseIndex + 1}`;
	};

	// Render compact switch info
	const renderCompactSwitchInfo = () => {
		if (stepNodeData.stepType !== Api.AutomationStepType.Switch || !stepNodeData.outputHandles) {
			return null;
		}

		const caseCount = stepNodeData.outputHandles.length;
		const casesToShow = Math.min(caseCount, 4);
		const overflow = Math.max(0, caseCount - 4);

		return (
			<div className={css(stepNodeStyles.switchCompactInfo)}>
				<div className={css(stepNodeStyles.switchBranchCount)}>
					{caseCount} {caseCount === 1 ? 'branch' : 'branches'}
				</div>
				<div className={css(stepNodeStyles.switchCaseIndicators)}>
					{stepNodeData.outputHandles.slice(0, casesToShow).map((handleId, index) => (
						<div
							key={handleId}
							className={css(stepNodeStyles.switchCaseDot)}
							style={{ backgroundColor: getCaseColor(index) }}
							aria-label={getCaseName(index)}
						/>
					))}
					{overflow > 0 && <span className={css(stepNodeStyles.switchOverflow)}>+{overflow}</span>}
				</div>
			</div>
		);
	};

	return (
		<div
			className={css(
				stepNodeStyles.nodeContainer,
				selected && stepNodeStyles.selected,
				validationPathState === 'path' && stepNodeStyles.validationPath,
				validationPathState === 'target' && stepNodeStyles.validationTarget
			)}
			role='button'
			tabIndex={0}
			aria-label={ariaLabel}
			aria-expanded={isExpanded}
			aria-describedby={`step-description-${id}`}
			onKeyPress={handleKeyPress}
		>
			<Handle type='target' position={targetPosition} id='target' className={css(stepNodeStyles.handle)} />

			{/* Visual indicator showing which case this child node belongs to */}
			{parentCaseInfo && (
				<div
					className={css(stepNodeStyles.parentCaseIndicator)}
					style={{
						backgroundColor: parentCaseInfo.caseColor || BRANCH_COLORS[0],
					}}
					aria-label={parentCaseAriaLabel}
				>
					{parentCaseBadgeLabel}
				</div>
			)}

			{contactColors && contactColors.length > 0 && (
				<div
					className={css(stepNodeStyles.contactBadgeContainer)}
					aria-label={`${contactCount || contactColors.length} contacts at this step`}
				>
					{contactColors.slice(0, 3).map((color, index) => (
						<div
							key={index}
							className={css(stepNodeStyles.contactColorDot)}
							style={{
								backgroundColor: color,
							}}
							aria-hidden='true'
						/>
					))}
					{(contactCount || contactColors.length) > 3 && (
						<span className={css(stepNodeStyles.contactOverflow)}>+{(contactCount || contactColors.length) - 3}</span>
					)}
				</div>
			)}

			{isExpanded ? (
				<div
					ref={contentRef}
					className={`${css(stepNodeStyles.expandedCard, isInvalid && stepNodeStyles.invalid)} nopan nodrag`}
					style={cardBorderStyle}
					id={`step-description-${id}`}
					onPointerDown={e => e.stopPropagation()}
				>
					<div className={css(stepNodeStyles.expandedHeader)}>
						<div className={css(stepNodeStyles.headerLeft)}>
							{stepTypeName === 'SwitchAutomationStep' || stepTypeName === 'TriggerAutomationStep' ? (
								<AutomationTemplateStepIndicatorIcon
									step={
										stepTypeName === 'SwitchAutomationStep'
											? ({ _type: stepTypeName } as Api.IAutomationStep)
											: undefined
									}
									stepType={
										stepTypeName === 'SwitchAutomationStep'
											? Api.AutomationStepType.Switch
											: Api.AutomationStepType.AddTag
									}
									type='default'
								/>
							) : (
								<AutomationStepIcon type={stepTypeName as Api.AutomationStepModelType} />
							)}
							<span className={css(stepNodeStyles.headerTitle)} title={stepName}>
								{stepName}
							</span>
							{isReadOnly && (
								<div className={css(stepNodeStyles.readOnlyBadge)} title='Read-only mode' aria-label='Read-only mode'>
									READ-ONLY
								</div>
							)}
						</div>
						<button
							className={css(stepNodeStyles.collapseButton)}
							onClick={e => {
								e.stopPropagation();
								setExpanded(false);
							}}
							aria-label='Collapse step details'
							title='Collapse'
						>
							<CollapseIcon />
						</button>
					</div>

					<div className={css(stepNodeStyles.expandedContent)}>
						<AutomationStepCardAdapter nodeId={id} data={stepNodeData} mode='full' readOnly={isReadOnly} />
					</div>
				</div>
			) : (
				<div
					ref={contentRef}
					className={css(stepNodeStyles.compactCard, isInvalid && stepNodeStyles.invalid)}
					style={cardBorderStyle}
					onClick={() => {
						setExpanded(true);
					}}
					id={`step-description-${id}`}
				>
					<div className={css(stepNodeStyles.compactHeader)}>
						{stepTypeName === 'SwitchAutomationStep' || stepTypeName === 'TriggerAutomationStep' ? (
							<AutomationTemplateStepIndicatorIcon
								step={
									stepTypeName === 'SwitchAutomationStep' ? ({ _type: stepTypeName } as Api.IAutomationStep) : undefined
								}
								stepType={
									stepTypeName === 'SwitchAutomationStep'
										? Api.AutomationStepType.Switch
										: Api.AutomationStepType.AddTag
								}
								type='small'
							/>
						) : (
							<AutomationStepIcon type={stepTypeName as Api.AutomationStepModelType} />
						)}
						<span className={css(stepNodeStyles.compactTitle)} title={stepName}>
							{stepName}
						</span>
						<button
							className={css(stepNodeStyles.expandButton)}
							onClick={e => {
								e.stopPropagation();
								setExpanded(true);
							}}
							aria-label='Expand step details'
							title='Expand'
						>
							<ExpandIcon />
						</button>
					</div>

					{stepNodeData.stepData?.schedule && <div className={css(stepNodeStyles.compactSchedule)}>{scheduleText}</div>}

					{renderCompactSwitchInfo()}
				</div>
			)}

			{/* Render multiple source handles for switch nodes */}
			{outputHandles.map((handleId: string, index: number) => (
				<Handle
					key={handleId}
					type='source'
					position={sourcePosition}
					id={handleId}
					className={css(stepNodeStyles.handle)}
					style={hasMultipleOutputs ? calculateHandleStyle(index, outputHandles.length) : undefined}
				/>
			))}
		</div>
	);
}
